import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  EntitlementMode,
  ExpenseCategory,
  ExpenseLineItem,
  ExpenseSource,
  InflowFrequency,
  WindfallAllocationMode,
} from '@economi/domain-contracts';
import {
  addDaysToIso,
  centsToSoles,
  classifyExpenseHorizonForCardInCycle,
  computeSolTemporalKpis,
  computeWindfallContributionCents,
  formatSolesFromCents,
  INITIAL_CYCLE_STATE,
  INITIAL_DEVELOPER_KEYS,
  INITIAL_ENTITLEMENT_STATE,
  INITIAL_RECURRING_EXPENSES,
  isDateWithinPeriod,
  MAX_USER_CREDIT_CARDS,
  percentToBp,
  renewCycleToNextPeriod,
  simulateExpenseImpact,
  solesToCents,
  synchronizeCycleRecurringExpenses,
  type BillingCycleHorizon,
  type CreditCardProfileDto,
  type DeveloperApiKeyItem,
  type EntitlementState,
  type ExpenseLedgerItem,
  type FinancialCycleState,
  type PaymentMethodType,
  type RecurringExpenseItem,
  type RecurringInflowItem,
  type RecurringValidityMode,
} from './services/solImpactEngine';
import {
  checkApiHealth,
  fetchSyncedCycleStateFromApi,
  pushSyncedCycleStateToApi,
  type AuthenticatedUserSession,
} from './services/apiClient';
import { AuthGateView } from './components/AuthGateView/AuthGateView';
import { HeaderBar, type ActiveViewTab } from './components/HeaderBar/HeaderBar';
import {
  PrimaryCapacityHero,
  type LastRegisteredExpenseFeedback,
} from './components/PrimaryCapacityHero/PrimaryCapacityHero';
import { KpiTelemetryGrid } from './components/KpiTelemetryGrid/KpiTelemetryGrid';
import { TrajectoryChart } from './components/TrajectoryChart/TrajectoryChart';
import { SolImpactSimulator } from './components/SolImpactSimulator/SolImpactSimulator';
import { ExpenseQuickLedger } from './components/ExpenseQuickLedger/ExpenseQuickLedger';
import { ExpensesAnalyticsView } from './components/ExpensesAnalyticsView/ExpensesAnalyticsView';
import {
  PENDING_RECEIPT_STORAGE_KEY,
  ReceiptScannerView,
  type ExternalCapturedCameraFile,
} from './components/ReceiptScannerView/ReceiptScannerView';
import { CycleAndInflowsView } from './components/CycleAndInflowsView/CycleAndInflowsView';
import { RecurringExpensesView } from './components/RecurringExpensesView/RecurringExpensesView';
import { EntitlementAndApiView } from './components/EntitlementAndApiView/EntitlementAndApiView';
import { InfoPopover } from './components/InfoPopover/InfoPopover';
import styles from './App.module.css';

const SESSION_STORAGE_KEY = 'economi_auth_session_v1';
const ACTIVE_TAB_STORAGE_KEY = 'economi_active_tab_v2';
const CYCLE_STATE_STORAGE_KEY = 'economi_cycle_state_v2_clean';

function hasConfiguredFinancialData(state: FinancialCycleState): boolean {
  return (
    state.recurringInflows.length > 0 ||
    (state.recurringExpenses ?? []).length > 0 ||
    (state.creditCards ?? []).length > 0 ||
    state.windfallInflows.length > 0 ||
    state.expenses.length > 0
  );
}

function normalizeCycleState(
  parsed: Partial<FinancialCycleState> | null | undefined
): FinancialCycleState {
  if (!parsed) return INITIAL_CYCLE_STATE;
  let nextState: FinancialCycleState = {
    ...INITIAL_CYCLE_STATE,
    ...parsed,
    creditCards: (parsed.creditCards ?? []).slice(0, MAX_USER_CREDIT_CARDS),
    recurringInflows: parsed.recurringInflows ?? [],
    recurringExpenses: parsed.recurringExpenses ?? INITIAL_RECURRING_EXPENSES,
    windfallInflows: parsed.windfallInflows ?? [],
    expenses: parsed.expenses ?? [],
  };

  // Si la fecha actual ya superó la fecha de renovación del ciclo (y el usuario tiene datos configurados),
  // avanzamos automáticamente el ciclo y descontamos de inmediato los gastos recurrentes del nuevo período.
  const todayIso = new Date().toISOString().slice(0, 10);
  let safetyCounter = 0;
  while (
    hasConfiguredFinancialData(nextState) &&
    todayIso > nextState.renewalDateIso.slice(0, 10) &&
    safetyCounter < 12
  ) {
    nextState = renewCycleToNextPeriod(nextState);
    safetyCounter += 1;
  }

  return synchronizeCycleRecurringExpenses(nextState);
}

export function App() {
  const urlParams = new URLSearchParams(window.location.search);
  const urlAction = urlParams.get('action');
  const urlToken = urlParams.get('token') ?? '';
  const urlEmail = urlParams.get('email') ?? '';
  const isSetPasswordLink = urlAction === 'set-password' && urlToken.length > 0;

  const [authSession, setAuthSession] = useState<AuthenticatedUserSession | null>(
    () => {
      if (isSetPasswordLink) return null;
      try {
        const raw =
          window.localStorage.getItem(SESSION_STORAGE_KEY) ??
          window.sessionStorage.getItem(SESSION_STORAGE_KEY);
        return raw ? (JSON.parse(raw) as AuthenticatedUserSession) : null;
      } catch {
        return null;
      }
    }
  );

  const [activeTab, setActiveTabState] = useState<ActiveViewTab>(() => {
    try {
      if (window.sessionStorage.getItem(PENDING_RECEIPT_STORAGE_KEY)) {
        return 'RECEIPT_OCR_SCANNER';
      }
      const savedTab = (window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) ??
        window.sessionStorage.getItem(
          ACTIVE_TAB_STORAGE_KEY
        )) as ActiveViewTab | null;
      if (
        savedTab === 'DASHBOARD_TELEMETRY' ||
        savedTab === 'EXPENSES_ANALYTICS' ||
        savedTab === 'RECEIPT_OCR_SCANNER' ||
        savedTab === 'CYCLE_AND_INFLOWS' ||
        savedTab === 'RECURRING_EXPENSES' ||
        savedTab === 'ENTITLEMENT_AND_API'
      ) {
        return savedTab;
      }
    } catch {
      // ignore
    }
    return 'DASHBOARD_TELEMETRY';
  });

  const setActiveTab = (nextTab: ActiveViewTab) => {
    setActiveTabState(nextTab);
    try {
      window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, nextTab);
      window.sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, nextTab);
    } catch {
      // ignore
    }
  };

  const [cycleState, setCycleState] = useState<FinancialCycleState>(() => {
    try {
      const raw =
        window.localStorage.getItem(CYCLE_STATE_STORAGE_KEY) ??
        window.sessionStorage.getItem(CYCLE_STATE_STORAGE_KEY);
      if (!raw) return INITIAL_CYCLE_STATE;
      const parsed = JSON.parse(raw) as FinancialCycleState;
      return normalizeCycleState(parsed);
    } catch {
      return INITIAL_CYCLE_STATE;
    }
  });

  const cycleStateRef = useRef<FinancialCycleState>(cycleState);
  cycleStateRef.current = cycleState;

  const isHydratedFromCloudRef = useRef<boolean>(false);
  const isApplyingRemoteStateRef = useRef<boolean>(false);
  const inFlightCloudPushCountRef = useRef<number>(0);
  const lastLocalMutationAtRef = useRef<number>(0);
  const localMutationVersionRef = useRef<number>(0);

  // Limpieza de claves de navegador obsoletas (sin borrar ni resetear datos del usuario en servidor)
  useEffect(() => {
    try {
      window.sessionStorage.removeItem('economi_cycle_state_v1');
      window.localStorage.removeItem('economi_cycle_state_v1');
      window.sessionStorage.removeItem('economi_pending_receipt_validation_v1');
      window.localStorage.removeItem('economi_pending_receipt_validation_v1');
    } catch {
      // ignore storage errors
    }
  }, []);

  // Sincronización bidireccional con PostgreSQL (/api/v1/state/sync) entre Computadora y Celular
  useEffect(() => {
    if (!authSession?.user?.email) {
      isHydratedFromCloudRef.current = false;
      return;
    }

    let cancelled = false;

    const syncWithCloud = async (isInitialMount = false) => {
      if (
        !isInitialMount &&
        (inFlightCloudPushCountRef.current > 0 ||
          Date.now() - lastLocalMutationAtRef.current < 5000)
      ) {
        return;
      }

      const mutationVersionAtStart = localMutationVersionRef.current;
      const res = await fetchSyncedCycleStateFromApi<FinancialCycleState>();
      if (cancelled) return;

      if (
        !isInitialMount &&
        (localMutationVersionRef.current !== mutationVersionAtStart ||
          inFlightCloudPushCountRef.current > 0 ||
          Date.now() - lastLocalMutationAtRef.current < 5000)
      ) {
        return;
      }

      if (!res.ok) {
        isHydratedFromCloudRef.current = true;
        return;
      }

      const remoteState =
        res.hasData && res.cycleState
          ? normalizeCycleState(res.cycleState)
          : null;
      const localState = cycleStateRef.current;

      if (remoteState && hasConfiguredFinancialData(remoteState)) {
        const remoteJson = JSON.stringify(remoteState);
        const rawRemoteJson = JSON.stringify(res.cycleState);
        const localJson = JSON.stringify(localState);
        if (remoteJson !== localJson) {
          isApplyingRemoteStateRef.current = true;
          setCycleState(remoteState);
        }
        isHydratedFromCloudRef.current = true;
        if (remoteJson !== rawRemoteJson) {
          inFlightCloudPushCountRef.current += 1;
          void pushSyncedCycleStateToApi(remoteState).finally(() => {
            inFlightCloudPushCountRef.current = Math.max(
              0,
              inFlightCloudPushCountRef.current - 1
            );
          });
        }
      } else if (hasConfiguredFinancialData(localState)) {
        // La computadora del usuario tiene datos configurados en localStorage y el servidor aún está vacío:
        // subimos automáticamente el estado local a PostgreSQL para que el celular lo reciba de inmediato.
        isHydratedFromCloudRef.current = true;
        inFlightCloudPushCountRef.current += 1;
        void pushSyncedCycleStateToApi(localState).finally(() => {
          inFlightCloudPushCountRef.current = Math.max(
            0,
            inFlightCloudPushCountRef.current - 1
          );
        });
      } else {
        if (remoteState) {
          const remoteJson = JSON.stringify(remoteState);
          const localJson = JSON.stringify(localState);
          if (remoteJson !== localJson) {
            isApplyingRemoteStateRef.current = true;
            setCycleState(remoteState);
          }
        }
        isHydratedFromCloudRef.current = true;
      }
    };

    void syncWithCloud(true);

    const handleWindowFocus = () => {
      void syncWithCloud(false);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncWithCloud(false);
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authSession?.user?.email]);

  // Persistencia local + subida automática a PostgreSQL ante cualquier cambio del usuario
  useEffect(() => {
    try {
      const serialized = JSON.stringify(cycleState);
      window.localStorage.setItem(CYCLE_STATE_STORAGE_KEY, serialized);
      window.sessionStorage.setItem(CYCLE_STATE_STORAGE_KEY, serialized);
    } catch {
      // ignore
    }

    if (isApplyingRemoteStateRef.current) {
      isApplyingRemoteStateRef.current = false;
      return;
    }

    localMutationVersionRef.current += 1;
    lastLocalMutationAtRef.current = Date.now();

    if (authSession?.user?.email && isHydratedFromCloudRef.current) {
      inFlightCloudPushCountRef.current += 1;
      void pushSyncedCycleStateToApi(cycleState).finally(() => {
        inFlightCloudPushCountRef.current = Math.max(
          0,
          inFlightCloudPushCountRef.current - 1
        );
      });
    }
  }, [cycleState, authSession?.user?.email]);

  const [entitlement, setEntitlement] = useState<EntitlementState>(
    INITIAL_ENTITLEMENT_STATE
  );
  const [developerKeys, setDeveloperKeys] =
    useState<ReadonlyArray<DeveloperApiKeyItem>>(INITIAL_DEVELOPER_KEYS);
  const [apiOnline, setApiOnline] = useState<boolean>(false);
  const [apiLatencyMs, setApiLatencyMs] = useState<number>(0);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showDetailedKpis, setShowDetailedKpis] = useState<boolean>(false);
  const [lastRegisteredImpact, setLastRegisteredImpact] =
    useState<LastRegisteredExpenseFeedback | null>(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false);
  const [externalCameraFile, setExternalCameraFile] =
    useState<ExternalCapturedCameraFile | null>(null);

  useEffect(() => {
    let mounted = true;
    void checkApiHealth().then((health) => {
      if (!mounted) return;
      setApiOnline(health.online);
      setApiLatencyMs(health.latencyMs);
      if (health.entitlementMode || health.allowedPilotEmails) {
        setEntitlement((prev) => ({
          ...prev,
          mode: health.entitlementMode ?? prev.mode,
          activeUserEmail: health.allowedPilotEmails?.[0] ?? prev.activeUserEmail,
          pilotAllowlist: health.allowedPilotEmails
            ? [
                {
                  ...prev.pilotAllowlist[0]!,
                  email: health.allowedPilotEmails[0],
                },
                {
                  ...prev.pilotAllowlist[1]!,
                  email: health.allowedPilotEmails[1],
                },
              ]
            : prev.pilotAllowlist,
        }));
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  const snapshot = useMemo(() => computeSolTemporalKpis(cycleState), [cycleState]);

  const handleAddExpense = (params: {
    amountSoles: number;
    merchantName: string;
    issuerRuc?: string | null;
    receiptNumber?: string | null;
    category: ExpenseCategory;
    description: string;
    source: ExpenseSource;
    r2ReceiptKey?: string;
    receiptImageDataUrl?: string | null;
    occurredAtIso?: string;
    isInevitable?: boolean;
    lineItems?: ReadonlyArray<ExpenseLineItem>;
    paymentMethod?: PaymentMethodType;
    creditCardId?: string | null;
    cardLastFourDigits?: string | null;
    billingCycleHorizon?: BillingCycleHorizon;
  }) => {
    const amountCents = solesToCents(params.amountSoles);
    const impact = simulateExpenseImpact(snapshot, params.amountSoles);
    const effectiveOccurredAtIso = (
      params.occurredAtIso?.trim() || cycleState.referenceDateIso
    ).slice(0, 10);

    const effectiveCreditCardId = params.creditCardId ?? null;
    const effectivePaymentMethod: PaymentMethodType = 'CREDIT_CARD';
    const effectiveHorizon: BillingCycleHorizon =
      params.billingCycleHorizon ??
      classifyExpenseHorizonForCardInCycle(
        cycleState,
        effectiveOccurredAtIso,
        effectiveCreditCardId
      );

    const effectiveLineItems: ReadonlyArray<ExpenseLineItem> =
      params.lineItems && params.lineItems.length > 0
        ? params.lineItems
        : [
            {
              description: params.description.trim() || params.merchantName,
              quantity: 1,
              totalCents: amountCents,
            },
          ];

    const newEntry: ExpenseLedgerItem = {
      id: `exp-${Date.now()}`,
      merchantName: params.merchantName,
      issuerRuc: params.issuerRuc ?? null,
      receiptNumber: params.receiptNumber ?? null,
      description: params.description,
      category: params.category,
      amountCents,
      occurredAtIso: effectiveOccurredAtIso,
      source: params.source,
      r2ReceiptKey: params.r2ReceiptKey,
      receiptImageDataUrl: params.receiptImageDataUrl ?? null,
      isInevitable: params.isInevitable,
      paymentMethod: effectivePaymentMethod,
      creditCardId: effectiveCreditCardId,
      cardLastFourDigits: params.cardLastFourDigits ?? null,
      billingCycleHorizon: effectiveHorizon,
      lineItems: effectiveLineItems,
      dailyCompressionCents:
        effectiveHorizon === 'NEXT_CYCLE'
          ? 0
          : impact.dailyAllowanceReductionCents,
      budgetHoursConsumed:
        effectiveHorizon === 'NEXT_CYCLE' ? 0 : impact.budgetTimeCostHours,
    };

    setCycleState((prev) =>
      synchronizeCycleRecurringExpenses({
        ...prev,
        expenses: [newEntry, ...prev.expenses],
      })
    );

    const belongsToActivePeriod = isDateWithinPeriod(
      effectiveOccurredAtIso,
      cycleState.startDateIso,
      cycleState.renewalDateIso
    );
    const matchedCard = effectiveCreditCardId
      ? (cycleState.creditCards ?? []).find(
          (c) => c.id === effectiveCreditCardId
        ) ?? null
      : null;

    if (belongsToActivePeriod) {
      setLastRegisteredImpact({
        merchantName: params.merchantName,
        expenseAmountCents: amountCents,
        capacityPercentConsumed:
          effectiveHorizon === 'NEXT_CYCLE'
            ? 0
            : impact.capacityPercentConsumedByExpense,
        remainingCapCents:
          effectiveHorizon === 'NEXT_CYCLE'
            ? snapshot.remainingCapCents
            : impact.remainingCapAfterExpenseCents,
        remainingCapacityPercent:
          effectiveHorizon === 'NEXT_CYCLE'
            ? snapshot.remainingSpendingCapacityPercent
            : impact.newRemainingSpendingCapacityPercent,
        effectiveSpendingCapCents: snapshot.effectiveSpendingCapCents,
        cardAlias: matchedCard
          ? `${matchedCard.alias} (${matchedCard.issuerBank})`
          : null,
        billingCycleHorizon: effectiveHorizon,
      });
      setToastMessage(null);
    } else {
      setToastMessage(
        `Gasto "${params.merchantName}" (${formatSolesFromCents(
          amountCents
        )}) registrado con fecha de emisión ${effectiveOccurredAtIso} (agrupado en el período ${effectiveOccurredAtIso.slice(
          0,
          7
        )}).`
      );
    }
  };

  const handleUpdateExpense = (
    expenseId: string,
    updates: {
      merchantName?: string;
      issuerRuc?: string | null;
      receiptNumber?: string | null;
      occurredAtIso?: string;
      category?: ExpenseCategory;
      description?: string;
      amountSoles?: number;
      isInevitable?: boolean;
      creditCardId?: string | null;
      cardLastFourDigits?: string | null;
      lineItems?: ReadonlyArray<ExpenseLineItem>;
    }
  ) => {
    lastLocalMutationAtRef.current = Date.now();
    localMutationVersionRef.current += 1;

    setCycleState((prev) => {
      const targetExisting = prev.expenses.find((e) => e.id === expenseId);
      const parsedAmountSoles =
        updates.amountSoles !== undefined ? Number(updates.amountSoles) : NaN;
      const hasValidAmountUpdate =
        Number.isFinite(parsedAmountSoles) && parsedAmountSoles > 0;

      const updatedExpenses = prev.expenses.map((exp) => {
        if (exp.id !== expenseId) return exp;

        const nextAmountCents = hasValidAmountUpdate
          ? Math.max(1, solesToCents(parsedAmountSoles))
          : exp.amountCents;
        const nextOccurredAtIso = (
          updates.occurredAtIso?.trim() || exp.occurredAtIso
        ).slice(0, 10);
        const nextCreditCardId =
          updates.creditCardId !== undefined
            ? updates.creditCardId
            : (exp.creditCardId ?? null);
        const matchedCard = nextCreditCardId
          ? (prev.creditCards ?? []).find((c) => c.id === nextCreditCardId) ??
            null
          : null;
        const nextLastFour =
          updates.cardLastFourDigits !== undefined
            ? updates.cardLastFourDigits
            : (matchedCard?.lastFourDigits || exp.cardLastFourDigits || null);

        const nextHorizon = classifyExpenseHorizonForCardInCycle(
          prev,
          nextOccurredAtIso,
          nextCreditCardId
        );
        const impact = simulateExpenseImpact(
          snapshot,
          centsToSoles(nextAmountCents)
        );
        const nextMerchantName =
          updates.merchantName !== undefined
            ? updates.merchantName.trim() || exp.merchantName
            : exp.merchantName;
        const nextDescription =
          updates.description !== undefined
            ? updates.description
            : exp.description;

        const existingItems = exp.lineItems ?? [];
        let nextLineItems: ReadonlyArray<ExpenseLineItem>;
        if (updates.lineItems && updates.lineItems.length > 0) {
          nextLineItems = updates.lineItems;
        } else if (existingItems.length <= 1) {
          const single = existingItems[0];
          nextLineItems = [
            {
              description:
                single &&
                single.description !== exp.description &&
                single.description !== exp.merchantName
                  ? single.description
                  : nextDescription.trim() || nextMerchantName,
              quantity: single?.quantity ?? 1,
              totalCents: nextAmountCents,
            },
          ];
        } else if (nextAmountCents !== exp.amountCents) {
          const prevSum = existingItems.reduce(
            (acc, li) => acc + li.totalCents,
            0
          );
          if (prevSum > 0) {
            let allocated = 0;
            nextLineItems = existingItems.map((li, idx, arr) => {
              if (idx === arr.length - 1) {
                return {
                  ...li,
                  totalCents: Math.max(1, nextAmountCents - allocated),
                };
              }
              const scaled = Math.max(
                1,
                Math.round((li.totalCents * nextAmountCents) / prevSum)
              );
              allocated += scaled;
              return {
                ...li,
                totalCents: scaled,
              };
            });
          } else {
            nextLineItems = existingItems;
          }
        } else {
          nextLineItems = existingItems;
        }

        return {
          ...exp,
          merchantName: nextMerchantName,
          issuerRuc:
            updates.issuerRuc !== undefined ? updates.issuerRuc : exp.issuerRuc,
          receiptNumber:
            updates.receiptNumber !== undefined
              ? updates.receiptNumber
              : exp.receiptNumber,
          occurredAtIso: nextOccurredAtIso,
          category: updates.category ?? exp.category,
          description: nextDescription,
          amountCents: nextAmountCents,
          isInevitable:
            updates.isInevitable !== undefined
              ? updates.isInevitable
              : exp.isInevitable,
          paymentMethod: 'CREDIT_CARD' as const,
          creditCardId: nextCreditCardId,
          cardLastFourDigits: nextLastFour,
          billingCycleHorizon: nextHorizon,
          lineItems: nextLineItems,
          dailyCompressionCents:
            nextHorizon === 'NEXT_CYCLE'
              ? 0
              : impact.dailyAllowanceReductionCents,
          budgetHoursConsumed:
            nextHorizon === 'NEXT_CYCLE' ? 0 : impact.budgetTimeCostHours,
        };
      });

      const activeMonthIso = prev.startDateIso.slice(0, 7);
      const linkedRuleId =
        targetExisting?.recurringRuleId ??
        (targetExisting?.source === 'RECURRING_RULE' &&
        expenseId.startsWith('exp-rec-')
          ? (prev.recurringExpenses ?? []).find((r) =>
              expenseId.startsWith(`exp-rec-${r.id}-`)
            )?.id
          : undefined);

      const updatedRecurringExpenses = linkedRuleId
        ? (prev.recurringExpenses ?? []).map((rule) => {
            if (rule.id !== linkedRuleId) return rule;
            const nextRuleCents = hasValidAmountUpdate
              ? Math.max(1, solesToCents(parsedAmountSoles))
              : rule.defaultAmountCents;
            const nextCardId =
              updates.creditCardId !== undefined
                ? updates.creditCardId
                : rule.creditCardId;
            return {
              ...rule,
              merchantName:
                updates.merchantName !== undefined
                  ? updates.merchantName.trim() || rule.merchantName
                  : rule.merchantName,
              issuerRuc:
                updates.issuerRuc !== undefined
                  ? updates.issuerRuc
                  : rule.issuerRuc,
              category: updates.category ?? rule.category,
              defaultAmountCents: nextRuleCents,
              isInevitable:
                updates.isInevitable !== undefined
                  ? updates.isInevitable
                  : rule.isInevitable,
              isCreditCardExpense: Boolean(nextCardId),
              creditCardId: nextCardId ?? null,
              monthlyOverridesCents: {
                ...(rule.monthlyOverridesCents ?? {}),
                [activeMonthIso]: nextRuleCents,
              },
            };
          })
        : prev.recurringExpenses;

      return synchronizeCycleRecurringExpenses({
        ...prev,
        recurringExpenses: updatedRecurringExpenses,
        expenses: updatedExpenses,
      });
    });

    setToastMessage('Gasto actualizado y recalculado en el ciclo.');
  };

  const handleUpdateExpenseDate = (
    expenseId: string,
    nextOccurredAtIso: string
  ) => {
    const cleanDate = nextOccurredAtIso.trim().slice(0, 10);
    if (!cleanDate) return;

    setCycleState((prev) =>
      synchronizeCycleRecurringExpenses({
        ...prev,
        expenses: prev.expenses.map((exp) =>
          exp.id === expenseId
            ? {
                ...exp,
                occurredAtIso: cleanDate,
                billingCycleHorizon: classifyExpenseHorizonForCardInCycle(
                  prev,
                  cleanDate,
                  exp.creditCardId ?? null
                ),
              }
            : exp
        ),
      })
    );

    const inActivePeriod = isDateWithinPeriod(
      cleanDate,
      cycleState.startDateIso,
      cycleState.renewalDateIso
    );
    setToastMessage(
      inActivePeriod
        ? `Fecha de emisión actualizada a ${cleanDate} (contabilizado en el período activo).`
        : `Fecha de emisión actualizada a ${cleanDate} (agrupado en el período ${cleanDate.slice(
            0,
            7
          )}).`
    );
  };

  const handleAttachExpenseReceiptImage = (
    expenseId: string,
    receiptImageDataUrl: string | null
  ) => {
    setCycleState((prev) => ({
      ...prev,
      expenses: prev.expenses.map((exp) =>
        exp.id === expenseId
          ? {
              ...exp,
              receiptImageDataUrl,
            }
          : exp
      ),
    }));
    setToastMessage(
      receiptImageDataUrl
        ? 'Imagen de boleta/recibo vinculada al gasto y sincronizada.'
        : 'Imagen de boleta removida del gasto.'
    );
  };

  const handleDeleteExpense = (expenseId: string) => {
    setCycleState((prev) => ({
      ...prev,
      expenses: prev.expenses.filter((e) => e.id !== expenseId),
    }));
    setToastMessage('Gasto eliminado del ciclo.');
  };

  const handleUpdateCyclePolicy = (params: {
    spendingRatePercent: number;
    keepRateConstantAcrossPeriods: boolean;
    startDateIso?: string;
    renewalDateIso: string;
  }) => {
    const nextBp = percentToBp(params.spendingRatePercent);
    setCycleState((prev) =>
      synchronizeCycleRecurringExpenses({
        ...prev,
        spendingRateBp: nextBp,
        keepRateConstantAcrossPeriods: params.keepRateConstantAcrossPeriods,
        startDateIso: params.startDateIso ?? prev.startDateIso,
        renewalDateIso: params.renewalDateIso,
      })
    );
  };

  const handleManualRenewCycle = () => {
    setCycleState((prev) => {
      const renewed = renewCycleToNextPeriod(prev);
      const activeCount = (renewed.recurringExpenses ?? []).filter(
        (r) => r.isActive !== false
      ).length;
      setToastMessage(
        `Ciclo renovado al período ${renewed.startDateIso} → ${renewed.renewalDateIso}. Se descontaron automáticamente ${activeCount} gasto(s) recurrente(s) desde el Día 1 (${renewed.startDateIso}).`
      );
      return renewed;
    });
  };

  const handleAddRecurringInflow = (params: {
    name: string;
    amountSoles: number;
    frequency: InflowFrequency;
    occurrencesPerCycle: number;
    customIntervalDays?: number;
    renewalDayOfMonth: number;
    secondBillingDayOfMonth?: number;
    autoUpdateCycleRenewalDate?: boolean;
  }) => {
    const unitCents = solesToCents(params.amountSoles);
    const occurrences = Math.max(1, params.occurrencesPerCycle);
    const totalCycleCents = unitCents * occurrences;

    const newItem: RecurringInflowItem = {
      id: `rec-${Date.now()}`,
      name: params.name,
      amountPerOccurrenceCents: unitCents,
      occurrencesPerCycle: occurrences,
      amountCents: totalCycleCents,
      frequency: params.frequency,
      customIntervalDays: params.customIntervalDays,
      renewalDayOfMonth: params.renewalDayOfMonth,
      secondBillingDayOfMonth: params.secondBillingDayOfMonth,
      isPrimaryCycleAnchor: cycleState.recurringInflows.length === 0,
      isActive: true,
    };

    setCycleState((prev) => {
      let nextRenewalDateIso = prev.renewalDateIso;
      if (params.autoUpdateCycleRenewalDate) {
        const daysToAdd =
          params.frequency === 'WEEKLY'
            ? 7 * occurrences
            : params.frequency === 'BIWEEKLY'
              ? 15 * occurrences
              : params.frequency === 'CUSTOM_DAYS'
                ? Math.max(1, params.customIntervalDays ?? 14) * occurrences
                : 30;
        nextRenewalDateIso = addDaysToIso(prev.startDateIso, daysToAdd);
      }

      return synchronizeCycleRecurringExpenses({
        ...prev,
        renewalDateIso: nextRenewalDateIso,
        recurringInflows: [...prev.recurringInflows, newItem],
      });
    });

    setToastMessage(
      `Ingreso recurrente "${params.name}" (+${formatSolesFromCents(
        totalCycleCents
      )} en el ciclo) registrado.`
    );
  };

  const handleUpdateRecurringInflow = (
    ruleId: string,
    params: {
      name: string;
      amountSoles: number;
      frequency: InflowFrequency;
      occurrencesPerCycle: number;
      customIntervalDays?: number;
      renewalDayOfMonth: number;
      secondBillingDayOfMonth?: number;
    }
  ) => {
    const unitCents = solesToCents(params.amountSoles);
    const occurrences = Math.max(1, params.occurrencesPerCycle);
    const totalCycleCents = unitCents * occurrences;

    setCycleState((prev) => ({
      ...prev,
      recurringInflows: prev.recurringInflows.map((item) => {
        if (item.id !== ruleId) return item;
        return {
          ...item,
          name: params.name,
          amountPerOccurrenceCents: unitCents,
          occurrencesPerCycle: occurrences,
          amountCents: totalCycleCents,
          frequency: params.frequency,
          customIntervalDays: params.customIntervalDays,
          renewalDayOfMonth: params.renewalDayOfMonth,
          secondBillingDayOfMonth: params.secondBillingDayOfMonth,
        };
      }),
    }));

    setToastMessage(
      `Ingreso recurrente "${params.name}" actualizado (+${formatSolesFromCents(
        totalCycleCents
      )} en el ciclo).`
    );
  };

  const handleDeleteRecurringInflow = (ruleId: string) => {
    setCycleState((prev) => ({
      ...prev,
      recurringInflows: prev.recurringInflows.filter((i) => i.id !== ruleId),
    }));
    setToastMessage('Ingreso recurrente eliminado del ciclo.');
  };

  const handleAddWindfallInflow = (params: {
    description: string;
    amountSoles: number;
    allocationMode: WindfallAllocationMode;
    customAllocationPercent: number;
    receivedAtIso?: string;
  }) => {
    const amountCents = solesToCents(params.amountSoles);
    const customBp = percentToBp(params.customAllocationPercent);
    const split = computeWindfallContributionCents(
      amountCents,
      params.allocationMode,
      cycleState.spendingRateBp,
      customBp
    );
    const effectiveDate = params.receivedAtIso ?? cycleState.referenceDateIso;

    setCycleState((prev) => ({
      ...prev,
      windfallInflows: [
        {
          id: `wf-${Date.now()}`,
          description: params.description,
          amountCents,
          allocationMode: params.allocationMode,
          customAllocationBp: customBp,
          receivedAtIso: effectiveDate,
          capContributionCents: split.capContributionCents,
          protectedSavingsCents: split.protectedSavingsCents,
        },
        ...prev.windfallInflows,
      ],
    }));

    setToastMessage(
      `Ingreso extraordinario "${params.description}" (+${formatSolesFromCents(
        amountCents
      )}) procesado: +${formatSolesFromCents(
        split.capContributionCents
      )} al límite y +${formatSolesFromCents(
        split.protectedSavingsCents
      )} a reserva.`
    );
  };

  const handleUpdateWindfallInflow = (
    inflowId: string,
    params: {
      description: string;
      amountSoles: number;
      allocationMode: WindfallAllocationMode;
      customAllocationPercent: number;
      receivedAtIso?: string;
    }
  ) => {
    const amountCents = solesToCents(params.amountSoles);
    const customBp = percentToBp(params.customAllocationPercent);

    setCycleState((prev) => ({
      ...prev,
      windfallInflows: prev.windfallInflows.map((wf) => {
        if (wf.id !== inflowId) return wf;
        const split = computeWindfallContributionCents(
          amountCents,
          params.allocationMode,
          prev.spendingRateBp,
          customBp
        );
        return {
          ...wf,
          description: params.description,
          amountCents,
          allocationMode: params.allocationMode,
          customAllocationBp: customBp,
          receivedAtIso: params.receivedAtIso ?? wf.receivedAtIso,
          capContributionCents: split.capContributionCents,
          protectedSavingsCents: split.protectedSavingsCents,
        };
      }),
    }));

    setToastMessage(`Ingreso extraordinario "${params.description}" actualizado.`);
  };

  const handleDeleteWindfallInflow = (inflowId: string) => {
    setCycleState((prev) => ({
      ...prev,
      windfallInflows: prev.windfallInflows.filter((wf) => wf.id !== inflowId),
    }));
    setToastMessage('Ingreso extraordinario eliminado del ciclo.');
  };

  const handleUpsertCreditCard = (params: {
    alias: string;
    issuerBank: string;
    lastFourDigits?: string | null;
    colorHex?: string;
    billingCloseDayOfMonth: number;
    paymentDueDayOfMonth: number;
    monthlyLimitSoles?: number | null;
  }) => {
    const currentCards = cycleState.creditCards ?? [];
    if (currentCards.length >= MAX_USER_CREDIT_CARDS) {
      setToastMessage(
        `Has alcanzado el máximo de ${MAX_USER_CREDIT_CARDS} tarjetas de crédito permitidas.`
      );
      return;
    }

    const monthlyLimitCents =
      params.monthlyLimitSoles && params.monthlyLimitSoles > 0
        ? solesToCents(params.monthlyLimitSoles)
        : null;

    const newCard: CreditCardProfileDto = {
      id: `card-${Date.now()}`,
      alias: params.alias.trim(),
      issuerBank: params.issuerBank.trim() || 'Banco Emisor',
      lastFourDigits: params.lastFourDigits ?? '',
      colorHex: params.colorHex ?? '#10b981',
      billingCloseDayOfMonth: Math.max(
        1,
        Math.min(31, params.billingCloseDayOfMonth)
      ),
      paymentDueDayOfMonth: Math.max(
        1,
        Math.min(31, params.paymentDueDayOfMonth)
      ),
      monthlyLimitCents,
      isActive: true,
    };

    setCycleState((prev) =>
      synchronizeCycleRecurringExpenses({
        ...prev,
        creditCards: [...(prev.creditCards ?? []), newCard].slice(
          0,
          MAX_USER_CREDIT_CARDS
        ),
      })
    );

    setToastMessage(
      `Tarjeta "${newCard.alias}" (${newCard.issuerBank}) configurada: factura día ${newCard.billingCloseDayOfMonth} y paga día ${newCard.paymentDueDayOfMonth}.`
    );
  };

  const handleUpdateCreditCard = (
    cardId: string,
    params: {
      alias: string;
      issuerBank: string;
      lastFourDigits?: string | null;
      colorHex?: string;
      billingCloseDayOfMonth: number;
      paymentDueDayOfMonth: number;
      monthlyLimitSoles?: number | null;
      isActive?: boolean;
    }
  ) => {
    const monthlyLimitCents =
      params.monthlyLimitSoles && params.monthlyLimitSoles > 0
        ? solesToCents(params.monthlyLimitSoles)
        : null;

    setCycleState((prev) =>
      synchronizeCycleRecurringExpenses({
        ...prev,
        creditCards: (prev.creditCards ?? []).map((card) => {
          if (card.id !== cardId) return card;
          return {
            ...card,
            alias: params.alias.trim(),
            issuerBank: params.issuerBank.trim() || card.issuerBank,
            lastFourDigits:
              params.lastFourDigits !== undefined
                ? (params.lastFourDigits ?? '')
                : card.lastFourDigits,
            colorHex: params.colorHex ?? card.colorHex,
            billingCloseDayOfMonth: Math.max(
              1,
              Math.min(31, params.billingCloseDayOfMonth)
            ),
            paymentDueDayOfMonth: Math.max(
              1,
              Math.min(31, params.paymentDueDayOfMonth)
            ),
            monthlyLimitCents,
            isActive: params.isActive ?? card.isActive,
          };
        }),
      })
    );

    setToastMessage(`Tarjeta "${params.alias}" actualizada.`);
  };

  const handleDeleteCreditCard = (cardId: string) => {
    setCycleState((prev) =>
      synchronizeCycleRecurringExpenses({
        ...prev,
        creditCards: (prev.creditCards ?? []).filter((c) => c.id !== cardId),
        recurringExpenses: (prev.recurringExpenses ?? []).map((rule) =>
          rule.creditCardId === cardId
            ? {
                ...rule,
                isCreditCardExpense: false,
                creditCardId: null,
              }
            : rule
        ),
      })
    );
    setToastMessage('Tarjeta de crédito eliminada del ciclo.');
  };

  const handleAddRecurringExpense = (params: {
    name: string;
    merchantName: string;
    issuerRuc?: string;
    category: ExpenseCategory;
    frequency: InflowFrequency;
    customIntervalDays?: number;
    defaultAmountSoles: number;
    billingDayOfMonth: number;
    isInevitable: boolean;
    validityMode?: RecurringValidityMode;
    expirationDateIso?: string | null;
    isCreditCardExpense?: boolean;
    creditCardId?: string | null;
  }) => {
    const amountCents = solesToCents(params.defaultAmountSoles);
    const activeMonthIso = cycleState.startDateIso.slice(0, 7);
    const ruleId = `rexp-${Date.now()}`;

    const newRule: RecurringExpenseItem = {
      id: ruleId,
      name: params.name,
      merchantName: params.merchantName,
      issuerRuc: params.issuerRuc ?? null,
      category: params.category,
      frequency: params.frequency,
      customIntervalDays: params.customIntervalDays,
      defaultAmountCents: amountCents,
      billingDayOfMonth: params.billingDayOfMonth,
      isInevitable: params.isInevitable,
      validityMode: params.validityMode ?? 'INDEFINITE',
      expirationDateIso:
        params.validityMode === 'EXPIRES_ON_DATE'
          ? (params.expirationDateIso ?? null)
          : null,
      isCreditCardExpense: Boolean(
        params.isCreditCardExpense && params.creditCardId
      ),
      creditCardId:
        params.isCreditCardExpense && params.creditCardId
          ? params.creditCardId
          : null,
      isActive: true,
      monthlyOverridesCents: {
        [activeMonthIso]: amountCents,
      },
    };

    setCycleState((prev) =>
      synchronizeCycleRecurringExpenses({
        ...prev,
        recurringExpenses: [...(prev.recurringExpenses ?? []), newRule],
      })
    );

    setToastMessage(
      `Gasto recurrente "${params.name}" (-${formatSolesFromCents(
        amountCents
      )} · ${
        params.isInevitable ? 'Inevitable' : 'Controlable'
      }) registrado y descontado automáticamente en el ciclo (${cycleState.startDateIso.slice(
        0,
        10
      )}).`
    );
  };

  const handleUpdateRecurringExpense = (
    ruleId: string,
    params: {
      name: string;
      merchantName: string;
      issuerRuc: string | null;
      category: ExpenseCategory;
      frequency: InflowFrequency;
      customIntervalDays?: number;
      defaultAmountSoles: number;
      billingDayOfMonth: number;
      isInevitable: boolean;
      isActive: boolean;
      validityMode?: RecurringValidityMode;
      expirationDateIso?: string | null;
      isCreditCardExpense?: boolean;
      creditCardId?: string | null;
    }
  ) => {
    const nextDefaultCents = solesToCents(params.defaultAmountSoles);
    const activeMonthIso = cycleState.startDateIso.slice(0, 7);

    setCycleState((prev) => {
      const currentRules = prev.recurringExpenses ?? [];
      const updatedRules = currentRules.map((rule) => {
        if (rule.id !== ruleId) return rule;
        const previousDefault = rule.defaultAmountCents;
        const nextOverrides = { ...rule.monthlyOverridesCents };
        if (nextOverrides[activeMonthIso] === previousDefault) {
          nextOverrides[activeMonthIso] = nextDefaultCents;
        }
        const effectiveValidityMode =
          params.validityMode ?? rule.validityMode ?? 'INDEFINITE';
        return {
          ...rule,
          name: params.name,
          merchantName: params.merchantName,
          issuerRuc: params.issuerRuc,
          category: params.category,
          frequency: params.frequency,
          customIntervalDays: params.customIntervalDays,
          defaultAmountCents: nextDefaultCents,
          billingDayOfMonth: params.billingDayOfMonth,
          isInevitable: params.isInevitable,
          isActive: params.isActive,
          validityMode: effectiveValidityMode,
          expirationDateIso:
            effectiveValidityMode === 'EXPIRES_ON_DATE'
              ? (params.expirationDateIso ?? rule.expirationDateIso ?? null)
              : null,
          isCreditCardExpense: Boolean(
            params.isCreditCardExpense && params.creditCardId
          ),
          creditCardId:
            params.isCreditCardExpense && params.creditCardId
              ? params.creditCardId
              : null,
          monthlyOverridesCents: nextOverrides,
        };
      });

      return synchronizeCycleRecurringExpenses({
        ...prev,
        recurringExpenses: updatedRules,
        expenses: params.isActive
          ? prev.expenses
          : prev.expenses.filter((e) => e.recurringRuleId !== ruleId),
      });
    });

    setToastMessage(`Gasto recurrente "${params.name}" actualizado.`);
  };

  const handleDeleteRecurringExpense = (ruleId: string) => {
    setCycleState((prev) =>
      synchronizeCycleRecurringExpenses({
        ...prev,
        recurringExpenses: (prev.recurringExpenses ?? []).filter(
          (r) => r.id !== ruleId
        ),
        expenses: prev.expenses.filter((e) => e.recurringRuleId !== ruleId),
      })
    );
    setToastMessage('Gasto recurrente eliminado.');
  };

  const handleUpdateRecurringExpenseForMonth = (params: {
    ruleId: string;
    monthIso: string;
    amountSoles: number;
    applyAsNewDefault: boolean;
    clearMonthOverride?: boolean;
    isInevitable?: boolean;
  }) => {
    const nextAmountCents = solesToCents(params.amountSoles);

    setCycleState((prev) => {
      const currentRules = prev.recurringExpenses ?? [];
      const updatedRules = currentRules.map((rule) => {
        if (rule.id !== params.ruleId) return rule;
        const nextOverrides = { ...rule.monthlyOverridesCents };
        if (params.clearMonthOverride) {
          delete nextOverrides[params.monthIso];
        } else {
          nextOverrides[params.monthIso] = nextAmountCents;
        }
        return {
          ...rule,
          defaultAmountCents:
            params.applyAsNewDefault && nextAmountCents > 0
              ? nextAmountCents
              : rule.defaultAmountCents,
          isInevitable: params.isInevitable ?? rule.isInevitable,
          monthlyOverridesCents: nextOverrides,
        };
      });

      return synchronizeCycleRecurringExpenses({
        ...prev,
        recurringExpenses: updatedRules,
      });
    });

    setToastMessage(
      params.clearMonthOverride
        ? `Monto restaurado al valor base para ${params.monthIso}.`
        : `Gasto recurrente actualizado (${params.monthIso}: ${formatSolesFromCents(
            nextAmountCents
          )}).`
    );
  };

  const handleToggleEntitlementMode = (mode: EntitlementMode) => {
    setEntitlement((prev) => ({
      ...prev,
      mode,
      activeSubscriptionStatus:
        mode === 'SOFT_LAUNCH_ALLOWLIST' ? 'PILOT_UNLIMITED' : 'TRIALING',
    }));
    setToastMessage(
      mode === 'SOFT_LAUNCH_ALLOWLIST'
        ? 'Guardián B2C conmutado a MARCHA BLANCA ESTRICTA (Solo 2 correos autorizados).'
        : 'Guardián B2C conmutado a SUSCRIPCIÓN B2C CON FREE TRIAL (14 días gratis para nuevos usuarios).'
    );
  };

  const handleGenerateApiKey = (params: {
    label: string;
    scopes: ReadonlyArray<string>;
  }) => {
    const randomHex = Array.from({ length: 24 }, () =>
      Math.floor(Math.random() * 16).toString(16)
    ).join('');
    const newKey: DeveloperApiKeyItem = {
      id: `key-${Date.now()}`,
      label: params.label,
      keyPrefix: `eco_live_${randomHex.slice(0, 8)}`,
      fullTokenPreview: `eco_live_${randomHex}`,
      scopes: params.scopes,
      createdAtIso: new Date().toISOString(),
      lastUsedAtIso: null,
    };
    setDeveloperKeys((prev) => [newKey, ...prev]);
    setToastMessage(`Nueva Personal API Key generada: ${newKey.keyPrefix}...`);
  };

  const handleAuthenticated = (session: AuthenticatedUserSession) => {
    try {
      const serialized = JSON.stringify(session);
      window.localStorage.setItem(SESSION_STORAGE_KEY, serialized);
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, serialized);
    } catch {
      // ignore storage quota errors
    }
    setAuthSession(session);
    setEntitlement((prev) => ({
      ...prev,
      activeUserEmail: session.user.email,
    }));
    setToastMessage(
      `¡Bienvenido(a), ${session.user.fullName}! Sesión iniciada con ${session.user.email}.`
    );
  };

  const handleLogout = () => {
    setAuthSession(null);
    try {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  if (!authSession) {
    return (
      <AuthGateView
        initialMode={isSetPasswordLink ? 'SET_PASSWORD' : 'LOGIN'}
        initialEmailFromLink={urlEmail}
        initialTokenFromLink={urlToken}
        onAuthenticated={handleAuthenticated}
      />
    );
  }

  const activeRecurringExpensesCount = (
    cycleState.recurringExpenses ?? []
  ).filter((r) => r.isActive).length;

  const desktopNavOptions: ReadonlyArray<{
    readonly tab: ActiveViewTab;
    readonly title: string;
    readonly badge: string;
    readonly icon: string;
  }> = [
    {
      tab: 'DASHBOARD_TELEMETRY',
      title: '1. Resumen y Tarjetas',
      badge: `${snapshot.remainingSpendingCapacityPercent.toFixed(0)}% Libre`,
      icon: '📊',
    },
    {
      tab: 'EXPENSES_ANALYTICS',
      title: '2. Gastos y Gráficos',
      badge: `${cycleState.expenses.length} gastos`,
      icon: '📋',
    },
    {
      tab: 'CYCLE_AND_INFLOWS',
      title: '3. Ciclo y Tarjetas',
      badge: `${snapshot.spendingRatePercent}% Tope`,
      icon: '💰',
    },
    {
      tab: 'RECURRING_EXPENSES',
      title: '4. Gastos Recurrentes',
      badge: `${activeRecurringExpensesCount} fijos`,
      icon: '🔄',
    },
    {
      tab: 'RECEIPT_OCR_SCANNER',
      title: '5. Escáner Boletas IA',
      badge: 'Vision IA',
      icon: '🧾',
    },
    {
      tab: 'ENTITLEMENT_AND_API',
      title: '6. Acceso y API',
      badge: '/api/v1',
      icon: '🔑',
    },
  ];

  const mobileNavOptions: ReadonlyArray<{
    readonly tab: ActiveViewTab;
    readonly title: string;
    readonly badge: string;
    readonly icon: string;
  }> = [
    {
      tab: 'DASHBOARD_TELEMETRY',
      title: '1. Resumen y Tarjetas',
      badge: `${snapshot.remainingSpendingCapacityPercent.toFixed(0)}% Libre`,
      icon: '📊',
    },
    {
      tab: 'EXPENSES_ANALYTICS',
      title: '2. Gastos y Gráficos',
      badge: `${cycleState.expenses.length} gastos`,
      icon: '📋',
    },
    {
      tab: 'CYCLE_AND_INFLOWS',
      title: '3. Ciclo y Tarjetas',
      badge: `${snapshot.spendingRatePercent}% Tope`,
      icon: '💰',
    },
    {
      tab: 'RECURRING_EXPENSES',
      title: '4. Gastos Recurrentes',
      badge: `${activeRecurringExpensesCount} fijos`,
      icon: '🔄',
    },
    {
      tab: 'RECEIPT_OCR_SCANNER',
      title: '5. Escáner Boleta IA',
      badge: 'Boleta',
      icon: '🧾',
    },
    {
      tab: 'ENTITLEMENT_AND_API',
      title: '6. Acceso y API',
      badge: '/api/v1',
      icon: '🔑',
    },
  ];

  const handleSelectNavOption = (tab: ActiveViewTab) => {
    setActiveTab(tab);
    setIsMobileNavOpen(false);
  };

  return (
    <div className={styles.appShell}>
      <HeaderBar
        snapshot={snapshot}
        userEmail={authSession.user.email}
        userFullName={authSession.user.fullName}
        userPhone={authSession.user.phone}
        userOccupation={authSession.user.occupation}
        onLogout={handleLogout}
      />

      <div className={styles.workspaceBody}>
        {/* Menú Vertical Izquierdo — Solo Vista de Computadora */}
        <aside
          className={styles.desktopSidebar}
          aria-label="Navegación Principal (Vista Computadora)"
        >
          <div className={styles.sidebarHeaderTitle}>
            Menú Principal
          </div>
          <nav className={styles.verticalNavList}>
            {desktopNavOptions.map((option) => (
              <button
                key={option.tab}
                type="button"
                className={`${styles.verticalNavBtn} ${
                  activeTab === option.tab ? styles.verticalNavBtnActive : ''
                }`}
                onClick={() => handleSelectNavOption(option.tab)}
              >
                <div className={styles.verticalNavBtnTop}>
                  <span className={styles.verticalNavIcon}>{option.icon}</span>
                  <span className={styles.verticalNavLabel}>{option.title}</span>
                </div>
                <span className={styles.verticalNavBadge}>{option.badge}</span>
              </button>
            ))}
          </nav>
        </aside>

        <main className={styles.main}>
          {toastMessage && (
            <div className={styles.toastBanner} role="status">
              <span>{toastMessage}</span>
              <button
                type="button"
                className={styles.toastDismiss}
                onClick={() => setToastMessage(null)}
              >
                [Cerrar ×]
              </button>
            </div>
          )}

          {activeTab === 'DASHBOARD_TELEMETRY' && (
            <>
              {/* 1. Indicador Principal Protagonista + Resumen Inmediato Post-Gasto */}
              <PrimaryCapacityHero
                snapshot={snapshot}
                cycleState={cycleState}
                lastRegisteredImpact={lastRegisteredImpact}
                onDismissLastImpact={() => setLastRegisteredImpact(null)}
                onOpenSetupFlow={() => setActiveTab('CYCLE_AND_INFLOWS')}
                onOpenCardsConfig={() => setActiveTab('CYCLE_AND_INFLOWS')}
                onOpenRecurringExpensesFlow={() =>
                  setActiveTab('RECURRING_EXPENSES')
                }
                onOpenCameraCaptureFlow={() => setActiveTab('RECEIPT_OCR_SCANNER')}
                showDetailedKpis={showDetailedKpis}
                onToggleDetailedKpis={() => setShowDetailedKpis((prev) => !prev)}
              />

              {/* A. VISTA DE COMPUTADORA (> 900px) */}
              <div className={styles.desktopMonitoringStack}>
                <section className={styles.desktopMonitoringSection}>
                  <div className={styles.desktopMonitoringHeader}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
                      <h3 className={styles.captureHeaderTitle}>
                        📊 Indicadores e Impacto del Sol
                      </h3>
                      <InfoPopover title="Telemetría e Impacto Temporal del Sol">
                        Monitorea tus 7 KPIs en tiempo real, la trayectoria diaria frente al ritmo ideal y simula el impacto de cualquier compra antes de realizarla.
                      </InfoPopover>
                    </div>
                    <span className={styles.desktopModePill}>
                      7 KPIs en Vivo
                    </span>
                  </div>

                  <KpiTelemetryGrid snapshot={snapshot} />

                  <div className={styles.dashboardMiddleGrid}>
                    <TrajectoryChart snapshot={snapshot} />
                    <SolImpactSimulator
                      snapshot={snapshot}
                      onQuickRegisterSimulatedExpense={(exp) =>
                        handleAddExpense({
                          ...exp,
                          source: 'WEB_MANUAL',
                        })
                      }
                    />
                  </div>
                </section>

                <section className={styles.captureSectionCard}>
                  <div className={styles.captureHeaderBar}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
                      <h3 className={styles.captureHeaderTitle}>
                        📋 Bitácora Rápida ({cycleState.expenses.length})
                      </h3>
                      <InfoPopover title="Bitácora y Registro Rápido">
                        Revisa los últimos consumos sincronizados o registra un gasto rápido con tarjeta de crédito.
                      </InfoPopover>
                    </div>
                  </div>

                  <ExpenseQuickLedger
                    snapshot={snapshot}
                    expenses={cycleState.expenses}
                    cycleState={cycleState}
                    onAddExpense={handleAddExpense}
                    onUpdateExpense={handleUpdateExpense}
                    onDeleteExpense={handleDeleteExpense}
                  />
                </section>
              </div>

              {/* B. VISTA DE CELULAR (<= 900px) */}
              <div className={styles.mobileQuickCaptureStack}>
                <div className={styles.progressiveDisclosureBar}>
                  <div className={styles.progressiveDisclosureText}>
                    <span
                      className={styles.progressiveDisclosureTitle}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                    >
                      📊 7 KPIs, Gráfica y Simulador
                      <InfoPopover title="Indicadores Detallados">
                        Despliega los 7 indicadores del impacto del Sol en el tiempo, la curva de gasto diario y el simulador de compras.
                      </InfoPopover>
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles.progressiveDisclosureBtn}
                    onClick={() => setShowDetailedKpis((prev) => !prev)}
                  >
                    {showDetailedKpis ? '🔼 Ocultar' : '📊 Ver Detalle'}
                  </button>
                </div>

                {showDetailedKpis && (
                  <div className={styles.detailedSectionStack}>
                    <KpiTelemetryGrid snapshot={snapshot} />
                    <div className={styles.dashboardMiddleGrid}>
                      <TrajectoryChart snapshot={snapshot} />
                      <SolImpactSimulator
                        snapshot={snapshot}
                        onQuickRegisterSimulatedExpense={(exp) =>
                          handleAddExpense({
                            ...exp,
                            source: 'WEB_MANUAL',
                          })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'EXPENSES_ANALYTICS' && (
            <ExpensesAnalyticsView
              cycleState={cycleState}
              snapshot={snapshot}
              onAddExpense={handleAddExpense}
              onUpdateExpense={handleUpdateExpense}
              onUpdateExpenseDate={handleUpdateExpenseDate}
              onAttachExpenseReceiptImage={handleAttachExpenseReceiptImage}
              onDeleteExpense={handleDeleteExpense}
              onOpenScannerView={() => setActiveTab('RECEIPT_OCR_SCANNER')}
            />
          )}

          {activeTab === 'RECEIPT_OCR_SCANNER' && (
            <ReceiptScannerView
              snapshot={snapshot}
              cycleState={cycleState}
              externalCameraFile={externalCameraFile}
              onCancel={() => setActiveTab('DASHBOARD_TELEMETRY')}
              onConfirmScannedExpense={(scanned) => {
                handleAddExpense(scanned);
                setActiveTab('DASHBOARD_TELEMETRY');
              }}
            />
          )}

          {activeTab === 'CYCLE_AND_INFLOWS' && (
            <CycleAndInflowsView
              cycleState={cycleState}
              snapshot={snapshot}
              onUpdateCyclePolicy={handleUpdateCyclePolicy}
              onAddRecurringInflow={handleAddRecurringInflow}
              onUpdateRecurringInflow={handleUpdateRecurringInflow}
              onDeleteRecurringInflow={handleDeleteRecurringInflow}
              onAddWindfallInflow={handleAddWindfallInflow}
              onUpdateWindfallInflow={handleUpdateWindfallInflow}
              onDeleteWindfallInflow={handleDeleteWindfallInflow}
              onUpsertCreditCard={handleUpsertCreditCard}
              onUpdateCreditCard={handleUpdateCreditCard}
              onDeleteCreditCard={handleDeleteCreditCard}
              onOpenRecurringExpensesView={() =>
                setActiveTab('RECURRING_EXPENSES')
              }
              onRenewCycleNow={handleManualRenewCycle}
            />
          )}

          {activeTab === 'RECURRING_EXPENSES' && (
            <RecurringExpensesView
              cycleState={cycleState}
              creditCards={cycleState.creditCards ?? []}
              onAddRecurringExpense={handleAddRecurringExpense}
              onUpdateRecurringExpense={handleUpdateRecurringExpense}
              onDeleteRecurringExpense={handleDeleteRecurringExpense}
              onUpdateRecurringExpenseForMonth={
                handleUpdateRecurringExpenseForMonth
              }
              onOpenInflowsView={() => setActiveTab('CYCLE_AND_INFLOWS')}
            />
          )}

          {activeTab === 'ENTITLEMENT_AND_API' && (
            <EntitlementAndApiView
              entitlement={entitlement}
              developerKeys={developerKeys}
              onToggleEntitlementMode={handleToggleEntitlementMode}
              onGenerateApiKey={handleGenerateApiKey}
            />
          )}
        </main>
      </div>

      {/* Menú Flotante Redondo en Lado Derecho — Solo Vista Celular */}
      <div className={styles.mobileFloatingContainer}>
        {isMobileNavOpen && (
          <>
            <div
              className={styles.mobileFabBackdrop}
              onClick={() => setIsMobileNavOpen(false)}
              aria-hidden="true"
            />
            <nav
              className={styles.mobileFabMenu}
              aria-label="Menú Flotante de Opciones (Vista Celular)"
            >
              <div className={styles.mobileFabMenuTitle}>
                📱 Opciones en Celular (Cámara Rápida)
              </div>
              {mobileNavOptions.map((option) => (
                <button
                  key={option.tab}
                  type="button"
                  className={`${styles.mobileFabOptionBtn} ${
                    activeTab === option.tab ? styles.mobileFabOptionBtnActive : ''
                  }`}
                  onClick={() => handleSelectNavOption(option.tab)}
                >
                  <div className={styles.mobileFabOptionMain}>
                    <span>{option.icon}</span>
                    <span>{option.title}</span>
                  </div>
                  <span className={styles.mobileFabOptionBadge}>{option.badge}</span>
                </button>
              ))}
            </nav>
          </>
        )}

        <div className={styles.mobileFabButtonStack}>
          <label
            className={styles.mobileCameraFabTrigger}
            aria-label="Tomar foto rápida de boleta con la cámara"
            title="Tomar foto rápida de boleta"
          >
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className={styles.hiddenCameraInput}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                setIsMobileNavOpen(false);
                setActiveTab('RECEIPT_OCR_SCANNER');
                setExternalCameraFile({ file, id: Date.now() });
              }}
            />
            <svg
              width="25"
              height="25"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
              <circle cx="12" cy="13" r="3.5" />
            </svg>
          </label>

          <button
            type="button"
            className={`${styles.mobileFabTrigger} ${
              isMobileNavOpen ? styles.mobileFabTriggerActive : ''
            }`}
            onClick={() => setIsMobileNavOpen((prev) => !prev)}
            aria-expanded={isMobileNavOpen}
            aria-label="Abrir menú de opciones de navegación"
            title="Menú de opciones"
          >
            {isMobileNavOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      <footer className={styles.footer}>
        EconomiController B2C · Arquitectura Hexagonal · Node.js 24.22.1 LTS · Vite 8.3.4 ·
        Aritmética Entera en Céntimos (PEN) · Cloudflare R2 & Resend
      </footer>
    </div>
  );
}
